/**
 * Everything this slice draws inside a day, as three named entry points.
 *
 * ── The port landed on 14 September 2026, and the PENCIL came with it ────────────────────────
 * From 15 August the backend's `progressionRules` map was carried opaquely here — read once, held
 * untouched, emitted byte-identical — so a trainer's rule survived a save from the browser but
 * could not be READ. That is over: `entities/plan-draft` reads the map, the engine agrees with the
 * backend at all 97 vectors of its own fixture, and `ProgressionSheet` is the editor the mock's
 * `pane3` draws. The row's right end therefore carries the pencil the mock has always drawn, and
 * `renderSheet()` answers with a surface instead of `null`.
 *
 * What the statement CAN say is which axes carry a rule, because the map's keys are measurement
 * field names rather than rule documents — `ruleStatement` in `model/` decides it and never opens a
 * value. Two words per exercise rather than one uniform paragraph six times over, which is what
 * makes a day scannable again: duplicating a day copies its rules by value, so a trainer needs to be
 * able to see at a glance which of these rows progress and which do not.
 *
 * `renderMatrix()` still answers `null`, and that is a SCOPE line rather than a gap: the mock puts
 * the projection inside the sheet, and a second grid under the set table is its own surface with its
 * own canon. The sheet's own matrix is `SheetMatrix`.
 *
 * ── What it was, so the port knows what to restore ──────────────────────────────────────────
 * The 02.08 gallery does not put progression in one box. It puts the rule CHIP in the exercise
 * card's header, the "System progresji" group with its preview toggle in the card's 284px side
 * column, the cycle matrix under the set table, and the rule EDITOR in a full-width sheet over the
 * whole day (`Builder progresji v2`). Four places, one concern. `dayProgression()` built the render
 * functions for them and held no state itself — the state was `progressionDisclosure`'s, so nothing
 * about which sheet is out or which matrix is expanded reached a module with no business knowing
 * what a rule is.
 */

import { useEffect, useRef, type ReactNode, type RefObject } from 'react';

import {
  useExerciseProgressionRules,
  useExerciseUnreadableProgressionRules,
} from '~/entities/plan-draft';
import visuallyHidden from '~/shared/ui/visuallyHidden.module.css';

import { useOwedFocus, useProgressionDisclosure } from '../lib/progressionDisclosure';
import type { Translate } from '../model/matrixCellState';
import { ruleStatement } from '../model/ruleStatement';

import styles from './DayProgressionSlots.module.css';
import { ProgressionSheet } from './ProgressionSheet';
import { StatementChips } from './StatementChips';

const KEY = 'web:planBuilder.progression';

export interface DayProgressionOptions {
  readonly t: Translate;
  /** The reader's own notation — every number this slice prints goes through it. */
  readonly locale: string;
  /** The HOST's live region. This slice owns no announcer; the day dock already has one. */
  readonly announce: (message: string) => void;
}

/**
 * The two boxes the host lends the sheet: the EMPTY one it is portalled into, and the day content it
 * covers and takes out of play. Declared here as well as in `plan-day-programming` because a feature
 * may not import a feature — the page that composes the two is what knows they are the same shape.
 */
export interface DaySheetHost {
  readonly container: RefObject<HTMLElement | null>;
  readonly background: RefObject<HTMLElement | null>;
}

/** The three entry points, matching `features/plan-day-programming`'s `DayProgression`. */
export interface DayProgressionSlots {
  readonly renderPanel: (exerciseSlotToken: string) => ReactNode;
  readonly renderMatrix: (exerciseSlotToken: string) => ReactNode;
  readonly renderSheet: (host: DaySheetHost) => ReactNode;
}

/**
 * The exercise card's progression row — the 07.09 programator mock's foot, left column.
 *
 * The mock's 22px row, sized to its own contents: the label `PROGRESJA`, then one kind chip and one
 * field name for every rule the exercise carries. It was a two-line framed paragraph until 7 September 2026, then the mock's
 * strip, then a 44px tile stretched across a `1fr` track — which the owner read on 8 September 2026
 * as *"mega mierna … tak sztucznie wielki i suchy"*, and he was right: the mock fills that row with
 * a spring and a pencil; the pencil is drawn again since 14 September and the spring is still not,
 * because the tile is sized to its contents. The stylesheet's header carries the measurement.
 *
 * It is a STATEMENT plus ONE control: the pencil at its right end, which opens the rule sheet for
 * this exercise. Nothing else in the row presses.
 *
 * ── The row NAMES THE KIND again, and that is a correction (9 September 2026) ────────────────
 * The mock colours the chip `DYNAMICZNA` / `STATYCZNA` / `MANUALNA` from mobile's own three hues,
 * and this file said until now that the build could not follow, because it could not read a rule.
 * That was wrong, and the owner corrected it: what is frozen is the PROJECTION, not the document.
 * `type` arrives intact and means one thing, so the chip says which kind it is and the field beside
 * it says which measurement the rule writes — `ruleStatement`'s header carries the full argument and
 * the three fields that stay unread. `MANUALNA` is not drawn because no rule document says it: an
 * exercise with no map is "Bez progresji", which is a different statement and takes no hue.
 *
 * ── The pencil, and where the focus goes when the sheet closes ───────────────────────────────
 * It was absent from 15 August to 14 September because there was no editor behind it, and the tile
 * was reshaped twice in the meantime for exactly that reason. It is back now, and it is also the
 * element the closed sheet hands focus to — see the effect below.
 */
function StatementPanel({
  t,
  exerciseSlotToken,
}: {
  readonly t: Translate;
  readonly exerciseSlotToken: string;
}) {
  const view = ruleStatement(
    useExerciseProgressionRules(exerciseSlotToken),
    useExerciseUnreadableProgressionRules(exerciseSlotToken),
    t,
  );
  const openRule = useProgressionDisclosure((state) => state.openRule);
  const clearReturnFocus = useProgressionDisclosure((state) => state.clearReturnFocus);
  const isOwedFocus = useOwedFocus(exerciseSlotToken);
  const pencilRef = useRef<HTMLButtonElement>(null);

  /* The sheet hands its focus back through the STORE rather than through Base UI's own
   * `finalFocus`, because a close routed through the discard prompt leaves Base UI holding the
   * prompt's button as "the element that was focused when this opened". So the disclosure store
   * names the slot that was just closed and this pencil, which is still mounted, claims it back —
   * without it a keyboard trainer who pressed Escape would be left on `<body>`, and the NEXT Escape
   * would close the whole day. */
  useEffect(() => {
    if (!isOwedFocus) return;
    pencilRef.current?.focus();
    clearReturnFocus();
  }, [isOwedFocus, clearReturnFocus]);

  return (
    <div className={styles.row}>
      <span className={styles.label}>{t(`${KEY}.bar.title`)}</span>
      {/* The full sentence on `title` for a pointer, and as REAL TEXT beside the chip for everyone
        * else. It was an `aria-label` on this `<span>` until 7 September 2026, and a bare span has
        * the `generic` role, which PROHIBITS `aria-label` — the name was discarded and a screen
        * reader got the abbreviation the chip prints instead. `PatternBar`'s `<output>` and
        * `DayColumn`'s weekday head already carry the drawn/spoken pair this way.
        *
        * The hidden half is drawn only where the two DIFFER. An exercise with no rule prints its
        * whole statement in the chip already ("Bez progresji"), and a second copy of it would be
        * read out twice — the same defect the week's muscle bars had before their hidden sentence
        * was deleted. */}
      <StatementChips view={view} />
      {view.statement !== view.noRule && (
        <span className={visuallyHidden.srOnly}>{view.statement}</span>
      )}
      {/* The mock's own right end, back now that there is an editor to open. The row's tile is
        * sized to its contents, so the pencil follows the last chip rather than being pushed to a
        * far edge by a spring — see the stylesheet's header for what that room went to instead. */}
      <button
        ref={pencilRef}
        type="button"
        className={styles.pencil}
        aria-label={t(`${KEY}.sheet.open`, { exercise: view.statement })}
        onClick={() => {
          openRule(exerciseSlotToken);
        }}
      >
        <PencilGlyph />
      </button>
    </div>
  );
}

/**
 * Build the three entry points.
 *
 * A plain function rather than a hook: it is called once per render of the day form and calls no
 * hook of its own, so it can be handed to a component that renders a variable number of cards. The
 * store read lives inside `StatementPanel`, which is a component and may hold one.
 */
export function dayProgression({
  t,
  locale,
  announce,
}: DayProgressionOptions): DayProgressionSlots {
  return {
    renderPanel: (exerciseSlotToken) => (
      <StatementPanel key={exerciseSlotToken} t={t} exerciseSlotToken={exerciseSlotToken} />
    ),
    /* Deliberately empty — see the header. The projection lives INSIDE the sheet on this canon. */
    renderMatrix: () => null,
    renderSheet: ({ container, background }) => (
      <ProgressionSheet
        container={container}
        background={background}
        t={t}
        locale={locale}
        announce={announce}
      />
    ),
  };
}

/** The mock's 18px pencil. Drawn rather than an icon font, like every other glyph in this app. */
function PencilGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
      <path
        d="M4 20h4L19 9a2.1 2.1 0 0 0-3-3L5 17v3Z"
        stroke="currentColor"
        strokeWidth={1.8}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
